import { Box, Flex, IconButton, Text, useBreakpoint, useDisclosure } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrowseFiltersSidebar } from "./components/filters/BrowseFiltersSidebar.tsx";
import { BrowseResultsGrid } from "./components/BrowseResultsGrid.tsx";
import { BrowseTabs } from "./components/BrowseTabs.tsx";
import {
  useIsSmallBreakpoint,
  useMobileBreakpoint,
} from "../../utils/useBreakpoints.ts";
import { useRemoveQueries } from "../../services/axiosInstance.ts";
import {
  GET_ARTIST_LIST_QUERY_KEY,
  GET_COMIC_LIST_QUERY_KEY,
  GET_RELEASES_LIST_QUERY_KEY,
  GET_BUNDLES_LIST_QUERY_KEY,
} from "./api/browseScreenApi.ts";
import { useHookstate } from "@hookstate/core";
import { t } from "i18next";
import { ModeToggle } from "../../components/resultsGrid/ModeToggle.tsx";
import { LayoutStyle, BrowseTabType } from "../../types.ts";
import { SortMenus } from "../../components/sortMenus/SortMenus.tsx";

import { useTitleOnNavigation } from "../../hooks/useTitleOnNavigation.ts";
import { appStore } from "../../hookstate/appStore.ts";
import { buildUrlPath, buildSearchParams } from "./utils/browseUrlParser.ts";
import { useTopBarHeight } from "../../hooks/userTopBarHeight.ts";
import { useAccountData } from "../../hooks/useAccountData.ts";
import { useNavigate, useLocation } from "react-router-dom";
import { useVirtualScrollRestoration } from "../../hooks/useVirtualScrollRestoration.ts";
import { useBrowseFilters } from "./hooks/useBrowseFilters.ts";
import { useSearchV2API } from "./hooks/useSearchV2API.ts";
import { useSearchV2 } from "../search/hooks/useSearchContext.tsx";
import { isChannelContext } from "../search/types.ts";
import { FilterIcon } from "../../components/icons/FilterIcon.tsx";
import { readerNavigationContext } from "../../hookstate/readerNavigationContextStore.ts";
import { useSubHeaderScroll } from "../../hooks/useSubHeaderScroll.ts";
import { SafeSearchNotice } from "../../components/safeSearch/SafeSearchToggleCard.tsx";

const relevanceOrder = [{ id: "relevance", name: "Relevance" }];

export default function BrowseScreen() {
  const isSmall = useIsSmallBreakpoint();
  const topBarHeight = useTopBarHeight();
  const { stickyTop: subHeaderTop, stickyTransform, stickyTransition } =
    useSubHeaderScroll();
  const accountData = useAccountData();
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll restoration - different tabs/filters = different scroll positions
  // Normalize search params by sorting to ensure consistent key regardless of param order
  const normalizedSearch = new URLSearchParams(location.search);
  normalizedSearch.sort();
  const { onVisibleIndexChange, restoreToIndex } = useVirtualScrollRestoration({
    keySegment: normalizedSearch.toString(),
  });

  const breakpoint = useBreakpoint({ ssr: false });
  const isXlUp = ["xl", "2xl", "3xl", "4xl"].includes(breakpoint);
  const { isOpen: isSidebarOpen, onToggle: onSidebarToggle, onClose: onSidebarClose, onOpen: onSidebarOpen } = useDisclosure();
  const sidebarInitialized = useRef(false);
  useEffect(() => {
    if (!sidebarInitialized.current && breakpoint) {
      sidebarInitialized.current = true;
      if (isXlUp) onSidebarOpen();
    }
  }, [breakpoint]);

  // Derive tab directly from React Router location (not state)
  const tab = useMemo((): BrowseTabType => {
    const pathSegment = location.pathname.split("/")[1];
    switch (pathSegment) {
      case "creators":
        return BrowseTabType.creators;
      case "books":
        return BrowseTabType.releases;
      case "bundles":
        return BrowseTabType.bundles;
      default:
        return BrowseTabType.comics;
    }
  }, [location.pathname]);

  // URL-derived filter state
  const {
    filters,
    apiParams,
    filterMaps,
    setFilters,
    clearAllFilters,
    slugEntities,
    ready,
  } = useBrowseFilters(tab);

  // Navigate to a new tab, preserving current filters
  const setTab = useCallback(
    (newTab: BrowseTabType) => {
      if (!filterMaps || !filters) {
        const tabPaths: Record<BrowseTabType, string> = {
          [BrowseTabType.comics]: "/browse",
          [BrowseTabType.releases]: "/books",
          [BrowseTabType.creators]: "/creators",
          [BrowseTabType.bundles]: "/bundles",
        };
        // Preserve browse_key even in fallback path
        const currentParams = new URLSearchParams(location.search);
        const browseKey = currentParams.get("browse_key");
        const fallbackSearch = browseKey ? `?browse_key=${browseKey}` : "";
        navigate(`${tabPaths[newTab]}${fallbackSearch}`);
        return;
      }
      const path = buildUrlPath(filters, filterMaps, newTab);
      const params = buildSearchParams(filters, filterMaps, newTab);

      // Preserve params not managed by the filter system
      const currentParams = new URLSearchParams(location.search);
      for (const key of ["standalone", "browse_key"]) {
        const val = currentParams.get(key);
        if (val) params.set(key, val);
      }

      params.sort();
      const search = params.toString();
      navigate(search ? `${path}?${search}` : path);
    },
    [navigate, filters, filterMaps, location.search],
  );
  const [mode, setMode] = useState<LayoutStyle["mode"]>("grid");

  const [orders, setOrders] = useState<
    Array<{
      id: string;
      name: string;
    }>
  >([]);

  const removeQueries = useRemoveQueries(
    (() => {
      switch (tab) {
        case BrowseTabType.comics:
          return GET_COMIC_LIST_QUERY_KEY;
        case BrowseTabType.releases:
          return GET_RELEASES_LIST_QUERY_KEY;
        case BrowseTabType.bundles:
          return GET_BUNDLES_LIST_QUERY_KEY;
        default:
          return GET_ARTIST_LIST_QUERY_KEY;
      }
    })(),
  );

  const handleClearAll = useCallback(() => {
    clearAllFilters();
    removeQueries?.();
  }, [clearAllFilters, removeQueries]);

  useEffect(() => {
    if (!isSmall && mode === "list") {
      setMode("grid");
    }
  }, [isSmall]);

  const appInitSortingOrders = useHookstate(
    appStore.init.comic_browse_sort_orders,
  ).get({
    noproxy: true,
  });


  const sidebarMode = isXlUp ? "push" as const : "overlay" as const;
  const searchQuery = filters?.query ?? "";
  const isSearching = searchQuery.length > 0;

  // Channel context: merge channel filters (e.g. artist_id) into apiParams
  const { context: activeContext } = useSearchV2();
  const channelFilters = isChannelContext(activeContext) ? activeContext.filters : undefined;
  const mergedApiParams = useMemo(() => {
    if (!apiParams || !channelFilters) return apiParams;
    const merged = new URLSearchParams(apiParams);
    for (const [key, value] of Object.entries(channelFilters)) {
      merged.set(key, String(value));
    }
    return merged;
  }, [apiParams, channelFilters]);

  const accountReady = accountData !== undefined;
  const searchEnabled = ready && accountReady;

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    activeCount: searchActiveCount,
    queryId: searchQueryId,
  } = useSearchV2API(tab, searchQuery, searchEnabled, mergedApiParams);

  // Sync search query ID to navigation context store for analytics attribution
  useEffect(() => {
    if (searchQueryId) {
      readerNavigationContext.merge({ searchQueryId: String(searchQueryId) });
    }
  }, [searchQueryId]);


  // Update sort orders when ready, search query, or tab changes
  useEffect(() => {
    if (!ready) return;

    let baseOrders = appInitSortingOrders;

    // Filter out Featured and Recent options when Releases tab is selected
    if (tab === BrowseTabType.releases) {
      baseOrders = appInitSortingOrders.filter(
        (order) => order.name.toLowerCase() !== "featured" && order.name.toLowerCase() !== "recent",
      );
    }

    if (searchQuery) {
      setOrders([...baseOrders, ...relevanceOrder]);
    } else {
      setOrders([...baseOrders]);
    }
  }, [ready, searchQuery, tab, appInitSortingOrders]);

  const handleSortOrderChange = useCallback(
    (value: string) => {
      setFilters({ sortOrder: value });
    },
    [setFilters],
  );

  const handleSortDurationChange = useCallback(
    (value: string) => {
      setFilters({ sortDuration: value });
    },
    [setFilters],
  );

  const isSafeSearchOn = !accountData?.user || !accountData.user.show_mature_content;
  const showSafeSearchNotice = isSafeSearchOn;
  const safeSearchPb = tab === BrowseTabType.comics ? "0px" : "12px";

  const resultsGrid = (
    <BrowseResultsGrid
      header={
        !isSmall ? (
          // Desktop header: tabs + sort + filter icon in one row
          <Box
            position="sticky"
            top={subHeaderTop}
            transform={stickyTransform}
            transition={stickyTransition}
            zIndex={1}
            bg="charcoal.charcoal"
          >
            <BrowsePageTitle
              tab={tab}
              ready={ready}
              slugEntities={slugEntities}
              searchQuery={isSearching ? searchQuery : undefined}
            />
            <Flex
              justifyContent="space-between"
              align="flex-end"
              borderBottom="1px solid"
              borderColor="transparent.white.10"
              mr="16px"
            >
              <BrowseTabs
                active={tab}
                setActive={setTab}
              />
              <Flex align="center" gap="8px" pb="8px">
                <SortMenus
                  invalidate={removeQueries}
                  sortOrder={filters?.sortOrder}
                  sortDuration={filters?.sortDuration}
                  onSortOrderChange={handleSortOrderChange}
                  onSortDurationChange={handleSortDurationChange}
                  sortOrders={orders}
                />
                <IconButton
                  aria-label="Toggle filters"
                  icon={<FilterIcon boxSize="20px" />}
                  variant="ghost"
                  color={isSidebarOpen ? "blaze.300" : "neutral.400"}
                  size="sm"
                  w="34px"
                  h="34px"
                  borderRadius="8px"
                  _hover={{ bg: "transparent.white.10" }}
                  onClick={onSidebarToggle}
                />
              </Flex>
            </Flex>
            {showSafeSearchNotice && (
              <Box pt="12px" pb={safeSearchPb}>
                <SafeSearchNotice />
              </Box>
            )}
          </Box>
        ) : (
          // Mobile header: row 1 (tabs), row 2 (sort + mode toggle + filter icon)
          <Box
            px="16px"
            pb="8px"
            position="sticky"
            top={subHeaderTop}
            transform={stickyTransform}
            transition={stickyTransition}
            zIndex={1}
            bg="charcoal.charcoal"
          >
            <BrowsePageTitle
              tab={tab}
              ready={ready}
              slugEntities={slugEntities}
              searchQuery={isSearching ? searchQuery : undefined}
            />
            {/* Row 1: Tabs */}
            <Flex
              align="flex-end"
              borderBottom="1.5px solid"
              borderColor="transparent.white.20"
            >
              <BrowseTabs
                active={tab}
                setActive={setTab}
              />
            </Flex>
            {/* Row 2: Sort + Grid toggle + Filter icon */}
            <Flex align="center" justify="space-between" py="12px">
              <Flex align="center" gap="8px">
                <SortMenus
                  invalidate={removeQueries}
                  sortOrder={filters?.sortOrder}
                  sortDuration={filters?.sortDuration}
                  onSortOrderChange={handleSortOrderChange}
                  onSortDurationChange={handleSortDurationChange}
                  sortOrders={orders}
                />
                <ModeToggle mode={mode} setMode={setMode} />
              </Flex>
              <IconButton
                aria-label="Open filters"
                icon={<FilterIcon boxSize="20px" />}
                variant="ghost"
                w="34px"
                h="34px"
                minW="34px"
                borderRadius="md"
                color="neutral.400"
                _hover={{ bg: "transparent.white.10" }}
                onClick={onSidebarToggle}
              />
            </Flex>
            {showSafeSearchNotice && (
              <Box pb={safeSearchPb}>
                <SafeSearchNotice />
              </Box>
            )}
          </Box>
        )
      }
      tab={tab}
      mode={mode}
      gridHeightAdjustment={`${topBarHeight.get()}px`}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data={data as any}
      isLoading={isLoading}
      urlSettled={searchEnabled}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onVisibleIndexChange={onVisibleIndexChange}
      restoreToIndex={restoreToIndex}
      isSearching={isSearching}
    />
  );

  // Desktop layout: grid + sidebar in a row
  if (!isSmall) {
    return (
      <Flex
        direction="column"
        width="full"
        sx={{ WebkitOverflowScrolling: "touch" }}
      >
        <Flex gap="16px">
          <Box flex="1" minW={0}>
            {resultsGrid}
          </Box>
          {filters && (
            <BrowseFiltersSidebar
              tab={tab}
              filters={filters}
              onFilterChange={setFilters}
              onClose={onSidebarClose}
              onClearAll={handleClearAll}
              isOpen={isSidebarOpen}
              topOffset={topBarHeight.get()}
              mode={sidebarMode}
            />
          )}
        </Flex>
      </Flex>
    );
  }

  // Mobile layout: grid + fullscreen filter sidebar
  return (
    <Flex
      direction="column"
      gap={0}
      width="full"
      sx={{ WebkitOverflowScrolling: "touch" }}
    >
      {resultsGrid}
      {filters && (
        <BrowseFiltersSidebar
          tab={tab}
          filters={filters}
          onFilterChange={setFilters}
          onClose={onSidebarClose}
          onClearAll={handleClearAll}
          isOpen={isSidebarOpen}
          topOffset={topBarHeight.get()}
          mode="fullscreen"
          resultCount={searchActiveCount}
          isLoading={isLoading}
        />
      )}
    </Flex>
  );
}

interface BrowsePageTitleProps {
  tab: BrowseTabType;
  ready: boolean;
  slugEntities: Record<string, any>;
  searchQuery?: string;
}

function BrowsePageTitle({ tab, ready, slugEntities, searchQuery }: BrowsePageTitleProps) {
  const isMobile = useMobileBreakpoint();
  const browseTitle = determineBrowsePageTitle(tab, ready, slugEntities);
  const displayTitle = searchQuery
    ? `Results for \u201c${searchQuery}\u201d`
    : browseTitle;
  useTitleOnNavigation(displayTitle);

  return (
    <Flex direction="column" gap="4px" pt={isMobile ? 6 : 10} pb={2}>
      <Text
        as="h1"
        fontSize={isMobile ? "24px" : "5xl"}
        lineHeight={isMobile ? "1.3" : "45.6px"}
        letterSpacing=".5px"
        fontWeight="700"
        fontFamily="Bricolage Grotesque"
        color="dune.dune"
      >
        {displayTitle}
      </Text>
    </Flex>
  );
}

const TAB_TITLES: Record<BrowseTabType, string> = {
  [BrowseTabType.comics]: "Browse Series",
  [BrowseTabType.releases]: "Browse Releases",
  [BrowseTabType.creators]: "Browse Creators",
  [BrowseTabType.bundles]: "Browse Bundles",
};

const TAB_SUFFIX: Record<BrowseTabType, string> = {
  [BrowseTabType.comics]: "Titles",
  [BrowseTabType.releases]: "Releases",
  [BrowseTabType.creators]: "Creators",
  [BrowseTabType.bundles]: "Bundles",
};

function determineBrowsePageTitle(
  tab: BrowseTabType,
  ready: boolean,
  slugEntities: Record<string, any>,
): string {
  const {
    lang: language,
    type,
    genre: category,
  } = slugEntities ?? {};

  // No slug filters active — use tab default
  if ((!language && !category && !type) || !ready) {
    return TAB_TITLES[tab];
  }

  const tabSuffix = TAB_SUFFIX[tab];
  const props = { language, category, type, tabSuffix };

  if (language && category && type) {
    return t("screens.browse.browse_title_type_category_language", props);
  } else if (category && type) {
    return t("screens.browse.browse_title_type_category", props);
  } else if (type && language) {
    return t("screens.browse.browse_title_type_language", props);
  } else if (category && language) {
    return t("screens.browse.browse_title_category_language", props);
  } else if (language) {
    return t("screens.browse.browse_title_language", props);
  } else if (category) {
    return t("screens.browse.browse_title_category", props);
  } else if (type) {
    return t("screens.browse.browse_title_type", props);
  }

  return TAB_TITLES[tab];
}
